/**
 * MPP Elite indicator set — centralized `SharedIndicatorDef`s extracted from the
 * existing chart studies (so chart + scanner + watchlist share one math source)
 * plus the proprietary setups. Assembled into `MPP_INDICATORS` and appended to
 * `SHARED_INDICATORS` in ../index.ts.
 *
 * Grows phase by phase: Squeeze → Dots family → H/L Jump + RS → Bongo/RSMA
 * (daily + weekly) → proprietary 8.
 */
import type { SharedIndicatorDef } from "../types";
import { SQUEEZE_SIGNAL, RELEASE_SIGNAL, SQUEEZE_AGE, RELEASE_AGE } from "./squeeze";
import {
  UP_DOTS, DOWN_DOTS, EMS, EMS_DAILY_CROSS, EMS_AGE, BGU, BGU_AGE, PP, PP_AGE,
  MOBO, MOBO_AGE, DS, DS_AGE, KF, KF_AGE, KF_2DAY, PSAR, PSAR_AGE, GCH_RCL, GCH_RCL_AGE,
} from "./dots";
import { HL_JUMP } from "./highJump";
import { BONGO_INDICATORS } from "./bongo";
import { RSMA_INDICATORS } from "./rs";
import { OBV_D_AGE, OBV_D_TREND } from "./obv";
import { FORCE_KAHUNA_INDICATORS } from "./force";
import { PMC_INDICATORS } from "./pmc";
import { HLCD_INDICATORS } from "./hlcd";
import { PERSONS_VM_INDICATORS } from "./personsVm";
import { CPR_INDICATORS } from "./cpr";
import { JP_HISTOGRAM_INDICATORS } from "./jpHistogram";
import { LCC_INDICATORS } from "./lcc";
import { PIVOT_INDICATORS } from "./personsPivots";
import { PERSON_SCREENER_INDICATORS } from "./personScreener";
import { PPS_INDICATORS } from "./pps";
import { PROPRIETARY_INDICATORS } from "./proprietary";

export { SQUEEZE_SIGNAL, RELEASE_SIGNAL, SQUEEZE_AGE, RELEASE_AGE } from "./squeeze";
export { UP_DOTS, DOWN_DOTS, EMS, GCH_RCL } from "./dots";
export { CPR } from "./cpr";
export { HL_JUMP } from "./highJump";
export { BONGO_SIGNAL, BONGO_AGE } from "./bongo";
export { RSMA_DAILY, RSMA_DAILY_AGE } from "./rs";
export { PMC_INDICATORS } from "./pmc";
export { HLCD_INDICATORS } from "./hlcd";
export { PROPRIETARY_INDICATORS } from "./proprietary";

export const MPP_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  // "Afiat - Dots" group — ordered to match the chart Dots panel,
  // with Up/Down Dots on top; each signal is followed by its Age companion.
  UP_DOTS,
  DOWN_DOTS,
  EMS,
  EMS_DAILY_CROSS,
  EMS_AGE,
  ...CPR_INDICATORS,
  BGU,
  BGU_AGE,
  PP,
  PP_AGE,
  MOBO,
  MOBO_AGE,
  DS,
  DS_AGE,
  KF,
  KF_2DAY,
  KF_AGE,
  SQUEEZE_SIGNAL,
  RELEASE_SIGNAL,
  SQUEEZE_AGE,
  RELEASE_AGE,
  PSAR,
  PSAR_AGE,
  GCH_RCL,
  GCH_RCL_AGE,
  // Force / Kahuna (JS Combined Dots) — daily + weekly, configurable.
  ...FORCE_KAHUNA_INDICATORS,
  // "Market Timing" group (Bongo, HCD/LCD, H/L Jump) followed by the
  // "Pivot Studies" group (OBV MACD, Pivot Momentum) — kept contiguous so the two
  // headers sit together in the picker.
  ...BONGO_INDICATORS,
  ...HLCD_INDICATORS,
  HL_JUMP,
  ...PERSONS_VM_INDICATORS,
  ...JP_HISTOGRAM_INDICATORS,
  ...LCC_INDICATORS,
  ...PIVOT_INDICATORS,
  ...PERSON_SCREENER_INDICATORS,
  ...PPS_INDICATORS,
  ...RSMA_INDICATORS,
  OBV_D_TREND,
  OBV_D_AGE,
  ...PMC_INDICATORS,
  ...PROPRIETARY_INDICATORS,
];
